"use client"

import { useState, useEffect } from "react"
import { Button } from "../../components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import { Dialog, DialogContent, DialogTrigger } from "@/components/ui/dialog"
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { AlertCircle, AlertTriangle, Loader2, X } from "lucide-react"
import toast from "react-hot-toast"
import { emailCancellationReason, addCancellationReason } from "../../services/apiUser"
import { useCancelSubscription } from "../../hooks/useCancelSubscription"

function shuffleArray(array) {
  const newArray = [...array]
  for (let i = newArray.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1))
    ;[newArray[i], newArray[j]] = [newArray[j], newArray[i]]
  }
  return newArray
}

const VimeoEmbed = () => {
    return (
      <div className="relative aspect-video w-full overflow-hidden rounded-lg bg-muted">
        <div className="absolute inset-0">
          <iframe
            src="https://player.vimeo.com/video/1070474730?h=f3131ef4df&amp;badge=0&amp;autopause=0&amp;player_id=0&amp;app_id=58479&amp;autoplay=1"
            frameBorder="0"
            allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media"
            className="w-full h-full"
            title="Let's stay Friends!"
          ></iframe>
        </div>
      </div>
    );
  };
  

function CancelSubscriptionButton() {
  const [open, setOpen] = useState(false)
  const [step, setStep] = useState(1)
  const [mainReason, setMainReason] = useState("")
  const [error, setError] = useState("")
  const [selectedReason, setSelectedReason] = useState("")
  const [shuffledCategories, setShuffledCategories] = useState([])
  const [shuffledReasonsByCategory, setShuffledReasonsByCategory] = useState({})
  const [isFormValid, setIsFormValid] = useState(false)
  const [confirmationChecked, setConfirmationChecked] = useState(false)
  const [cancellationDate, setCancellationDate] = useState("")
  const [pending, setPending] = useState(false)
  const [emailSending, setEmailSending] = useState(false)
  const [continueLoading, setContinueLoading] = useState(false)
  const [bookCallLoading, setBookCallLoading] = useState(false)
  const { mutate: cancel, isLoading } = useCancelSubscription()

  const MIN_REASON_LENGTH = 20

  const feedbackReasons = {
    "Lack of Results": [
      "Not finding enough relevant contract opportunities",
      "AI-chat isn't helpful",
      "Proposal generation did not meet expectations",
    ],
    Pricing: ["Too expensive for the value provided", "Found a better alternative at a lower price"],
    Quality: ["Hard to use or navigate", "Technical issues or bugs"],
    Unused: ["Not using it enough to justify the cost", "Business needs have changed"],
    "Customer Support": ["Unsatisfactory support experience"],
  }

  const itemsToLose = [
    "All Saved Proposals",
    "All Saved Contracts",
    "All saved AI scheduled queries",
    "All Integrations",
    "AI Credits",
  ]

  useEffect(() => {
    if (open && step === 3) {
      const futureDate = new Date()
      futureDate.setDate(futureDate.getDate() + 30)

      const options = {
        month: "short",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
      }
      setCancellationDate(futureDate.toLocaleString("en-US", options))
    }
  }, [open, step])

  useEffect(() => {
    if (open && step === 2) {
      const categories = Object.keys(feedbackReasons)
      const shuffled = shuffleArray(categories)
      setShuffledCategories(shuffled)

      const reasonsByCategory = {}
      shuffled.forEach((category) => {
        const reasons = feedbackReasons[category]
        reasonsByCategory[category] = reasons.length > 0 ? shuffleArray(reasons) : []
      })
      setShuffledReasonsByCategory(reasonsByCategory)
    }
  }, [open, step])

  useEffect(() => {
    const isReasonValid = mainReason.length >= MIN_REASON_LENGTH
    const hasSelectedReason = selectedReason !== ""
    setIsFormValid(isReasonValid && hasSelectedReason)
  }, [mainReason, selectedReason])

  const handleReasonChange = (e) => {
    setMainReason(e.target.value)
    if (e.target.value.length > 0 && e.target.value.length < MIN_REASON_LENGTH) {
      setError(`Please provide more details (minimum ${MIN_REASON_LENGTH} characters)`)
    } else {
      setError("")
    }
  }

  const handleRadioChange = (value) => {
    setSelectedReason(value)
  }

  const handleContinueToCancellation = () => {
    setContinueLoading(true)
    setTimeout(() => {
      setStep(2)
      setContinueLoading(false)
    }, 500)
  }

  const handleSubmitFeedback = async () => {
    if (!isFormValid) return

    setPending(true)
    const data = {
      mainReason,
      selectedReason,
    }
    try {
      await addCancellationReason(data)
      setStep(3)
    } catch (error) {
      console.error("Error adding cancellation reason:", error)
    } finally {
      setPending(false)
    }
  }

  const handleCancellationSubmit = async () => {
    try {
      setEmailSending(true)
      await emailCancellationReason({ mainReason, selectedReason })
      setOpen(false)
      toast.success("Email sent, the Samsearch team will contact you ASAP!")
    } catch (error) {
      console.error("Error sending cancellation reason:", error)
    } finally {
      setEmailSending(false)
    }
  }

  const handleConfirmCancellation = async () => {
    try {
      cancel(undefined, {
        onSuccess: () => {
          setOpen(false)
          resetForm()
          toast.success("Subscription cancelled - Sorry to see you leave :(")
        },
        onError: (error) => {
          console.error("Error cancelling subscription:", error)
          toast.error("Error cancelling subscription")
        },
      })
    } catch (error) {
      console.error("Unexpected error:", error)
      toast.error("Unexpected error occurred")
    }
  }

  const resetForm = () => {
    setStep(1)
    setMainReason("")
    setError("")
    setSelectedReason("")
    setConfirmationChecked(false)
  }

  const handleClose = () => {
    setOpen(false)
    resetForm()
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(newOpen) => {
        setOpen(newOpen)
        if (!newOpen) {
          resetForm()
        }
      }}
    >
      <DialogTrigger asChild>
        <Button variant="outline" className="text-destructive border-destructive/30 hover:bg-destructive/10">
          Cancel Subscription
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[650px] max-h-[90vh] overflow-y-auto">
        <div className="absolute right-4 top-4">
          <button
            onClick={handleClose}
            className="rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
          >
            <X className="h-4 w-4" />
            <span className="sr-only">Close</span>
          </button>
        </div>

        {step === 1 ? (
          <div className="space-y-4">
            <h2 className="text-xl font-semibold">Cancel Subscription</h2>

            <div className="space-y-2">
              <h3 className="text-lg font-medium">Thinking About Leaving? Let's Talk First.</h3>
              <p className="text-sm text-muted-foreground">
                We'd love to understand how we can improve your experience. Before you go, watch this quick message from
                our team.
              </p>
            </div>

            <div className="relative aspect-video w-full overflow-hidden rounded-lg bg-muted">
              <div className="absolute inset-0 flex items-center justify-center">
                <VimeoEmbed> </VimeoEmbed>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row justify-end gap-2">
              <Button
                variant="outline"
                onClick={handleContinueToCancellation}
                className="border-destructive/30 text-destructive hover:bg-destructive/10"
                disabled={continueLoading}
              >
                {continueLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Processing...
                  </>
                ) : (
                  "Continue Cancellation"
                )}
              </Button>
              <Button
                variant="default"
                onClick={() => {
                  setBookCallLoading(true)
                  window.location.href = "https://zcal.co/humamhwr/feedback"
                }}
              >
                {bookCallLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Redirecting...
                  </>
                ) : (
                  "Book a Call Instead"
                )}
              </Button>
            </div>
          </div>
        ) : step === 2 ? (
          <div className="space-y-4">
            <h2 className="text-xl font-semibold">Cancel Subscription</h2>

            <div className="space-y-2">
              <h3 className="text-lg font-medium">Help Us Improve — Why Are You Cancelling?</h3>
              <p className="text-sm text-muted-foreground">
                Your feedback helps us refine the product. Please select the main reason for cancellation.
              </p>
            </div>

            {selectedReason === "" && (
              <div className="flex items-center text-sm text-amber-600 bg-amber-50 p-2 rounded-md">
                <AlertCircle className="h-4 w-4 mr-2" />
                <span>Please select a reason for cancellation</span>
              </div>
            )}

            <div className="space-y-6 max-h-[300px] overflow-y-auto pr-2">
              <RadioGroup value={selectedReason} onValueChange={handleRadioChange}>
                {shuffledCategories.map((category) => {
                  const shuffledReasons = shuffledReasonsByCategory[category] || []

                  return (
                    <div key={category} className="space-y-2 mb-4">
                      <h4 className="text-md font-medium text-muted-foreground">{category}</h4>
                      {shuffledReasons.length > 0 ? (
                        <div className="space-y-2 ml-2">
                          {shuffledReasons.map((reason) => {
                            const radioId = `${category}-${reason}`.replace(/\s+/g, "-").toLowerCase()
                            const radioValue = `${category}: ${reason}`

                            return (
                              <div key={radioId} className="flex items-center space-x-2">
                                <RadioGroupItem value={radioValue} id={radioId} />
                                <Label
                                  htmlFor={radioId}
                                  className="text-sm font-normal leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                                >
                                  {reason}
                                </Label>
                              </div>
                            )
                          })}
                        </div>
                      ) : category === "Proposal Issues" ? (
                        <div className="text-sm text-muted-foreground italic">
                          Please specify any proposal-related issues in the text field below.
                        </div>
                      ) : null}
                    </div>
                  )
                })}
              </RadioGroup>
            </div>

            <div className="space-y-2">
              <Label htmlFor="main-reason" className="text-sm font-medium">
                What's the main reason you're cancelling? <span className="text-destructive">*</span>
              </Label>
              <Textarea
                id="main-reason"
                placeholder="Please provide details about why you're cancelling..."
                value={mainReason}
                onChange={handleReasonChange}
                className={`min-h-[100px] ${error ? "border-destructive focus-visible:ring-destructive" : ""}`}
              />
              {error && (
                <div className="flex items-center text-sm text-destructive mt-1">
                  <AlertCircle className="h-4 w-4 mr-1" />
                  <span>{error}</span>
                </div>
              )}
              {!error && mainReason.length === 0 && (
                <div className="flex items-center text-sm text-amber-600 mt-1">
                  <AlertCircle className="h-4 w-4 mr-1" />
                  <span>Please tell us why you're cancelling</span>
                </div>
              )}
            </div>

            <div className="flex flex-col sm:flex-row justify-end gap-2 pt-2">
              <Button
                variant="outline"
                onClick={handleSubmitFeedback}
                className="border-destructive/30 text-destructive hover:bg-destructive/10"
                disabled={!isFormValid || pending}
              >
                {pending ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  "Continue to Cancellation"
                )}
              </Button>
              <Button variant="default" onClick={handleCancellationSubmit} disabled={!isFormValid || emailSending}>
                {emailSending ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Sending...
                  </>
                ) : (
                  "Send to Team & Wait"
                )}
              </Button>
            </div>

            {!isFormValid && (
              <div className="text-sm text-center text-muted-foreground">
                Please select a reason and provide detailed feedback to continue
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-6">
            <h2 className="text-xl font-semibold">Cancel Subscription</h2>

            <div className="flex items-center justify-center">
              <div className="bg-red-100 p-6 rounded-full">
                <AlertTriangle className="h-12 w-12 text-red-500" />
              </div>
            </div>

            <div className="text-center">
              <h3 className="text-xl font-medium text-red-500">
                Your Data Will Be Permanently Deleted after {cancellationDate}
              </h3>

              <p className="text-muted-foreground mt-3">
                After cancellation, you will lose access to your account with all the saved data. This action cannot be
                undone.
              </p>
            </div>

            <div className="space-y-1">
              <h4 className="text-lg font-medium">What you'll lose:</h4>
              <ul className="space-y-2">
                {itemsToLose.map((item, index) => (
                  <li key={index} className="flex items-center">
                    <X className="h-5 w-5 text-red-500 mr-2" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="flex items-start space-x-2 pt-4">
              <Checkbox
                id="confirmation-checkbox"
                checked={confirmationChecked}
                onCheckedChange={(checked) => setConfirmationChecked(checked === true)}
                className="mt-1"
              />
              <Label htmlFor="confirmation-checkbox" className="text-sm font-normal">
                I understand that all my data will be deleted and cannot be recovered.
              </Label>
            </div>

            <div className="flex flex-col sm:flex-row justify-end gap-2 pt-4">
              <Button variant="outline" onClick={handleClose} className="border-muted hover:bg-muted/10">
                Don't Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={handleConfirmCancellation}
                disabled={!confirmationChecked || isLoading}
                className="bg-red-500 hover:bg-red-600"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Cancelling...
                  </>
                ) : (
                  "Confirm Cancellation"
                )}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}

export default CancelSubscriptionButton
